import { API, graphqlOperation, Storage } from "aws-amplify";
import _ from "lodash";
import * as customMutations from "graphql/customMutations";
import { toast } from "../../components/Toast/Toast";
import {
  listAssets,
  createAsset,
  updateAsset,
  listOneQueryOfAssets,
  waitForProcessedAssetStatus,
  getAssetsOfCampaign,
  getApprovalsOfCampaign,
  getActiveUser,
  getApprover,
  getUserMatchingApprover,
  getAssetsOfCampaignContributor,
  sendInfoToCloudwatch,
} from "services/apiMiddleware";
import { addOrUpdateArrayItem, replaceArrayItem } from "services/utils";
import { t } from "../../i18n";
import { ORIGINAL_ASSETS_FILTER } from "constants/graphqlFilters";

const assets = {
  state: {
    isLoading: false,
    isAssetApproversLoading: false,
    isLazyLoading: false,
    error: null,
    items: [],
    approvalItems: [],
    assetApprovers: {},
    processedItems: [],
    allItemsLoaded: false,
    uploadProgressArr: [],
    isPollingProcessedItems: false,
    isDeletingAssets: false,
  },
  reducers: {
    reset: (state) => {
      return {
        ...state,
        isLoading: false,
        isLazyLoading: false,
        error: null,
        items: [],
        processedItems: [],
        approvalItems: [],
        allItemsLoaded: false,
        uploadProgressArr: [],
        isPollingProcessedItems: false,
        isDeletingAssets: false,
      };
    },
    resetModal: (state) => {
      return {
        ...state,
        isLoading: false,
        isLazyLoading: false,
        error: null,
        items: [],
        allItemsLoaded: false,
        uploadProgressArr: [],
        isPollingProcessedItems: false,
        isDeletingAssets: false,
      };
    },
    loadStart: (state) => {
      return {
        ...state,
        isLoading: true,
      };
    },
    loadStartAssetApprovers: (state) => {
      return {
        ...state,
        isAssetApproversLoading: true,
      };
    },
    startProcessedPolling: (state) => {
      return {
        ...state,
        isPollingProcessedItems: true,
      };
    },
    loadSuccess: (state, items, processedItems, approvalItems) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items,
        approvalItems,
        processedItems,
        allItemsLoaded: true,
      };
    },
    loadApprovalAssetsSuccess: (state, approvalItems) => {
      return {
        ...state,
        approvalItems,
      };
    },
    loadOneSuccess: (state, { foundItem, isLoading = false }) => {
      return {
        ...state,
        isLoading,
        error: null,
        items: addOrUpdateArrayItem(state.items, (item) => item.id === foundItem.id, foundItem),
      };
    },
    loadProcessedOneSuccess: (state, { foundItem, isLoading = false }) => {
      return {
        ...state,
        isLoading,
        error: null,
        processedItems: addOrUpdateArrayItem(
          state.processedItems,
          (item) => item.id === foundItem.id,
          foundItem
        ),
      };
    },
    loadLazyStart: (state) => {
      return {
        ...state,
        isLazyLoading: true,
      };
    },
    loadLazyQueryDone: (state, newItems) => {
      return {
        ...state,
        error: null,
        items: _.concat(state.items, newItems),
      };
    },
    loadLazySuccess: (state) => {
      return {
        ...state,
        error: null,
        isLazyLoading: false,
      };
    },
    createSuccess: (state, newItem) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items: [...state.items, newItem],
      };
    },
    removeCampaign: (state, campaignID) => {
      // Set 'campaign'-field to null each assets that have equal 'campaignID'
      // - used after campaign.delete - action
      // => allows assets deletion without reloading assets from graphql api.
      const assetsToEdit = state.items.filter((asset) => asset.campaignID === campaignID);
      const restAssets = state.items.filter((asset) => asset.campaignID !== campaignID);
      const editedAssets = assetsToEdit.map((asset) => {
        return { ...asset, campaign: null };
      });
      return { ...state, items: [...restAssets, ...editedAssets] };
    },
    updateSuccess: (state, updatedItem) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items: replaceArrayItem(state.items, (item) => item.id === updatedItem.id, updatedItem),
      };
    },
    updateProcessedSuccess: (state, updatedItem) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        processedItems: replaceArrayItem(
          state.processedItems,
          (item) => item.id === updatedItem.id,
          updatedItem
        ),
      };
    },
    deleteSuccess: (state, deletedId) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items: state.items.filter((item) => item.id !== deletedId),
      };
    },
    failure: (state, error) => {
      return {
        ...state,
        isLoading: false,
        error,
      };
    },
    loadAllFailure: (state, error) => {
      return {
        ...state,
        isLoading: false,
        error,
        allItemsLoaded: false,
      };
    },
    // Add a new upload progress state for asset
    addUploadProgressArr: (state, newUploadProgress) => {
      return {
        ...state,
        uploadProgressArr: [...state.uploadProgressArr, newUploadProgress],
      };
    },
    // Update upload progress
    updateUploadProgressArr: (state, updatedUploadProgress) => {
      return {
        ...state,
        uploadProgressArr: replaceArrayItem(
          state.uploadProgressArr,
          (item) => item.id === updatedUploadProgress.id,
          updatedUploadProgress
        ),
      };
    },
    loadAssetApproversSuccess: (state, assetApprovers) => {
      return {
        ...state,
        assetApprovers,
        isAssetApproversLoading: false,
      };
    },
    uploadProgressSuccess: (state) => {
      return {
        ...state,
        uploadProgressArr: [],
      };
    },
    stopLoading: (state) => {
      return {
        ...state,
        isLoading: false,
      };
    },
    stopProcessedPolling: (state) => {
      return {
        ...state,
        isPollingProcessedItems: false,
      };
    },
    deleteAssetsStart: (state) => {
      return { ...state, isDeletingAssets: true };
    },
    deleteAssetsSuccess: (state) => {
      return {
        ...state,
        isDeletingAssets: false,
      };
    },
  },
  effects: (dispatch) => ({
    /**
     * Loads all assets to state while having isLoading on the whole time
     * @param {object} [payload={}] - Optional payload object
     * @param {boolean} payload.force - Load can be forced regardless if all assets have been loaded before
     */
    async load(payload = {}, state) {
      try {
        // Return if load is not forced and all assets have already been loaded
        if (!payload.force && state.assets.allItemsLoaded) return;
        dispatch.assets.resetModal();
        dispatch.assets.loadStart();
        const { data } = await listAssets({ filter: ORIGINAL_ASSETS_FILTER });
        dispatch.assets.loadSuccess(data, state.assets.processedItems);
      } catch (error) {
        console.error(error);
        dispatch.assets.loadAllFailure(t("assets_load_error", { ns: "common" }));
      }
    },

    /**
     * Loads assets for a specific campaign to state
     * @param {string} campaignID - The ID of the campaign
     */
    async loadCampaignAssets(campaignID) {
      try {
        dispatch.assets.loadStart();
        // Change to get Campaign and load assets along, and then process the assets.
        const data = await getAssetsOfCampaign(campaignID);
        // TODO: Fix the above command.

        /*const { data } = await listAssets({
          filter: {
            campaignID: { eq: campaignID },
          },
        });*/

        const originalAssets = data.filter((item) => !item.media.includes("processed_assets"));
        const processedAssets = data.filter((item) => item.media.includes("processed_assets"));

        for (const asset of originalAssets)
          dispatch.assets.loadOneSuccess({ foundItem: asset, isLoading: true, original: false });
        for (const asset of processedAssets)
          dispatch.assets.loadProcessedOneSuccess({
            foundItem: asset,
            isLoading: true,
            original: false,
          });
        dispatch.assets.stopLoading();
      } catch (error) {
        console.error(error);
        dispatch.assets.failure(t("assets_load_error", { ns: "common" }));
      }
    },

    // Loads when campaign is opened in approvals tab
    async loadCampaignApprovalAssets(campaignID) {
      try {
        dispatch.assets.loadStart();
        const activeUser = await getActiveUser();
        const { data: contributorsApproverItems } = await getUserMatchingApprover(
          activeUser.data.username
        );

        let contributorsApproverIds = contributorsApproverItems.map((approver) => approver.id);
        let assets;
        if (activeUser.data.isContributor) {
          assets = await getAssetsOfCampaignContributor(campaignID);
        } else {
          assets = await getAssetsOfCampaign(campaignID);
        }
        let approvals;
        approvals = await getApprovalsOfCampaign(campaignID);

        if (activeUser.data.isContributor) {
          const contributorData = approvals.data.filter((approval) =>
            contributorsApproverIds.includes(approval.approverID)
          );
          approvals.data = contributorData;
        }
        const uniqueAssets = [];
        const allowedAssetIds = [...new Set(approvals.data.map((a) => a.assetID))];
        const uniqueIds = {};

        assets.forEach((asset) => {
          if (!uniqueIds[asset.id] && asset.viewers) {
            uniqueIds[asset.id] = true;
            uniqueAssets.push(asset);
          }
        });

        const filteredAssets = uniqueAssets.filter((asset) => allowedAssetIds.includes(asset.id));
        const assetsWithApprovals = filteredAssets.map((asset) => ({
          ...asset,
          approvals: approvals.data.filter((approval) => approval.assetID === asset.id),
        }));

        dispatch.assets.loadApprovalAssetsSuccess(assetsWithApprovals);
        dispatch.assets.stopLoading();
      } catch (error) {
        console.error(error);
        dispatch.assets.failure(t("assets_load_error", { ns: "common" }));
      }
    },

    //Loads when asset card is opened in admin approvals view
    //Must have asset with approvals [] as param
    async loadAssetAdditionalApproverInfo(assetWithApprovals) {
      dispatch.assets.loadStartAssetApprovers();
      let assetApprovers = {};
      const approvals = assetWithApprovals.approvals || [];
      const approverIds = approvals.map((a) => a.approverID);
      const uniqueApproverIds = [...new Set(approverIds)];

      for (const approverID of uniqueApproverIds) {
        const { data: approver } = await getApprover(approverID);
        assetApprovers[approverID] = approver;
      }
      dispatch.assets.loadAssetApproversSuccess(assetApprovers);
    },

    //Processed assets fetched for campaign
    async loadCampaignProcessedAssets(campaign) {
      try {
        dispatch.assets.startProcessedPolling();

        const { data } = await waitForProcessedAssetStatus(campaign);
        for (const asset of data) dispatch.assets.updateProcessedSuccess(asset);

        dispatch.assets.stopProcessedPolling();
      } catch (error) {
        console.error(error);
        dispatch.assets.failure(t("assets_load_error", { ns: "common" }));
      }
    },

    /**
     * Loads one query of assets to state and the rest in the background. isLoading is on for the first query only, isLazyLoading for the whole time
     * @param {object} [payload={}] - Optional payload object
     * @param {boolean} payload.force - Load can be forced even if all assets have been loaded before
     */
    async loadLazy(payload = {}, state) {
      try {
        // Return if load is not forced and all assets have already been loaded
        if (!payload.force && state.assets.allItemsLoaded) return;
        dispatch.assets.reset();
        dispatch.assets.loadStart();
        dispatch.assets.loadLazyStart();
        const result = await listOneQueryOfAssets({ filter: ORIGINAL_ASSETS_FILTER });
        dispatch.assets.loadSuccess(result.data, []);
        let nextToken = result.nextToken;
        // Load the rest one query at a time as long as the query returns nextToken
        if (nextToken) {
          do {
            const result = await listOneQueryOfAssets({
              nextToken,
              filter: ORIGINAL_ASSETS_FILTER,
            });
            dispatch.assets.loadLazyQueryDone(result.data);
            nextToken = result.nextToken;
          } while (nextToken);
        }
        dispatch.assets.loadLazySuccess();
      } catch (error) {
        console.error(error);
        dispatch.assets.loadAllFailure(t("assets_load_error", { ns: "common" }));
      }
    },
    async create({ createProps, media }) {
      try {
        dispatch.assets.loadStart();
        const uploadProgressObj = {
          id: _.uniqueId(`${createProps.name}_`),
          name: createProps.name,
          size: createProps.file.size,
          loaded: 0,
        };
        dispatch.assets.addUploadProgressArr(uploadProgressObj);
        const { data } = await createAsset(
          createProps,
          dispatch.assets.updateUploadProgressArr,
          uploadProgressObj
        );
        dispatch.assets.createSuccess(data);
        return data;
      } catch (error) {
        dispatch.assets.failure(t("asset_create_error", { ns: "common" }));
        throw error;
      }
    },
    async update({ updateProps, media }) {
      const UserRemovingAsset = await getActiveUser();
      try {
        dispatch.assets.loadStart();
        const { data } = await updateAsset(updateProps, media);
        dispatch.assets.updateSuccess(data);

        if (data && !data.media.includes("processed")) dispatch.assets.updateSuccess(data);
        if (data && data.media.includes("processed")) dispatch.assets.updateProcessedSuccess(data);

        // Send information to Cloudwatch
        await sendInfoToCloudwatch("remove-asset", UserRemovingAsset, data);

        return data;
      } catch (error) {
        dispatch.assets.failure(t("asset_update_error", { ns: "common" }));
        throw error;
      }
    },
    async delete(asset) {
      const UserDeletingAsset = await getActiveUser();
      try {
        dispatch.assets.loadStart();
        let relatedAssets = [];

        // Fetch all processed assets along with the original.
        // All these assets use the same thumbnail which we use for filtering.
        // ARRIVED assets can be missing a thumbnail when the upload failed, so
        // we fall back to the selected asset to keep deletion and telemetry intact.
        if (asset.thumbnail) {
          const { data = [] } = await listAssets({
            filter: {
              or: [{ thumbnail: { eq: asset.thumbnail } }],
            },
          });

          relatedAssets = data;
        }

        const assetsToDelete = relatedAssets.length > 0 ? relatedAssets : [asset];

        // Delete all assets in list from DynamoDB
        for (const item of assetsToDelete)
          await API.graphql(
            graphqlOperation(customMutations.deleteAsset, { input: { id: item.id } })
          );

        // Delete all asset files S3
        for (const item of assetsToDelete)
          if (item.media && item.media !== "temp") await Storage.remove(item.media);

        // Delete single thumbnail from S3
        if (asset.thumbnail && asset.thumbnail !== "temp") await Storage.remove(asset.thumbnail);
        // Send information to Cloudwatch
        await sendInfoToCloudwatch("delete-asset", UserDeletingAsset, assetsToDelete);

        // Update asset list
        dispatch.assets.deleteSuccess(asset.id);
      } catch (error) {
        console.error(error);
        dispatch.assets.failure(t("asset_delete_error", { ns: "common" }));
      }
    },

    async deleteProcessedAssets(assets) {
      const campaign = assets[0].campaign.id;
      dispatch.assets.deleteAssetsStart();
      try {
        let totalDeleted = 0;
        for (const item of assets) {
          await API.graphql(
            graphqlOperation(customMutations.deleteAsset, { input: { id: item.id } })
          );
        }

        // Delete all asset files S3
        for (const item of assets) {
          totalDeleted += 1;
          await Storage.remove(item.media);
        }

        if (totalDeleted === assets.length) {
          dispatch.assets.reset();
          dispatch.assets.loadCampaignAssets(campaign);
          dispatch.campaigns.loadOne(campaign);

          toast(undefined, "Assets successfully deleted");
        }
      } catch (error) {
        console.error(error);
        dispatch.assets.failure(t("asset_delete_error", { ns: "common" }));
      }
    },
  }),
};

export default assets;
